import React, { useState, useEffect, useCallback } from 'react';
import { Search, User, Clock, Star } from 'lucide-react';
import BaseModal from './BaseModal';

const MovieSearchModal = ({
  isOpen,
  onClose,
  onMovieSelect,
  supabaseUrl,
  supabaseAnonKey,
  initialSearchTerm = '',
  showSkipButton = false,
  onSkip,
}) => {
  const [searchQuery, setSearchQuery] = useState(initialSearchTerm);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');

  const searchMovies = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError('');
    setHasSearched(true);

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/search-movies-2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ title: searchQuery, mediaType: mediaTypeFilter }),
      });

      const data = await response.json();

      if (data.success) {
        setSearchResults(data.movies || []);
      } else {
        setError(data.error || 'Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Movie search error:', error);
      setError('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, mediaTypeFilter, supabaseUrl, supabaseAnonKey]);

  // Reset state when modal opens with new search term
  useEffect(() => {
    if (isOpen) {
      setSearchQuery(initialSearchTerm);
      setSearchResults([]);
      setError('');
      setHasSearched(false);
      setMediaTypeFilter('all');
    }
  }, [isOpen, initialSearchTerm]);

  // Auto-search when modal opens with initialSearchTerm
  useEffect(() => {
    if (isOpen && initialSearchTerm && !hasSearched) {
      searchMovies();
    }
  }, [isOpen, initialSearchTerm, hasSearched, searchMovies]);

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      searchMovies();
    }
  };

  const selectMovie = movie => {
    onMovieSelect({
      title: movie.Title,
      year: movie.Year,
      director: movie.Director,
      actors: movie.Actors && movie.Actors !== 'N/A' ? movie.Actors : '',
      genre: movie.Genre,
      runtime: movie.Runtime,
      description: movie.Plot,
      poster_url: movie.poster_url || (movie.Poster !== 'N/A' ? movie.Poster : null),
      imdbID: movie.imdbID,
      imdbRating: movie.imdbRating && movie.imdbRating !== 'N/A' ? movie.imdbRating : '',
      country: movie.Country && movie.Country !== 'N/A' ? movie.Country : '',
      language: movie.Language && movie.Language !== 'N/A' ? movie.Language : '',
      rated: movie.Rated && movie.Rated !== 'N/A' ? movie.Rated : '',
      released: movie.Released && movie.Released !== 'N/A' ? movie.Released : '',
      writer: movie.Writer && movie.Writer !== 'N/A' ? movie.Writer : '',
      producer: movie.Producer && movie.Producer !== 'N/A' ? movie.Producer : '', // NOW FROM TMDB CREDITS!
      distributor:
        movie.Distributor ||
        (movie.production_companies && movie.production_companies.length > 0
          ? movie.production_companies.map(company => company.name).join(', ')
          : ''),
      boxoffice: movie.BoxOffice && movie.BoxOffice !== 'N/A' ? movie.BoxOffice : '',

      Type: movie.Type || 'movie',

      // NEW TMDB FIELDS
      tmdb_id: movie.tmdb_id || null,
      production_companies: movie.production_companies || [],
      budget: movie.budget || null,
      trailer_url: movie.trailer_url || '',
      tagline: movie.tagline || '',
      alternate_title: movie.alternate_title || '',
      backdrop_url: movie.backdrop_url || null,

      // TV-specific fields
      network: movie.network || '',
      number_of_seasons: movie.number_of_seasons || null,
      number_of_episodes: movie.number_of_episodes || null,
      show_status: movie.show_status || '',
      last_air_date: movie.last_air_date || '',
    });
    onClose();
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Search Titles" maxWidth="2xl" zIndex={70}>
      {/* Custom Header with Skip Button */}
      {showSkipButton && (
        <div className="flex justify-end mb-4">
          <button
            onClick={onSkip}
            className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
          >
            Skip Movie
          </button>
        </div>
      )}

      {/* Search Input */}
      <div className="p-6">
        <div className="flex space-x-3">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder='Enter title or "tmdb:12345"...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full pl-10 pr-4 py-3 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
              autoFocus
            />
          </div>
          <button
            onClick={searchMovies}
            disabled={!searchQuery.trim() || isSearching}
            className="px-3 sm:px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 flex-shrink-0"
          >
            {isSearching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">Searching...</span>
              </>
            ) : (
              <>
                <Search size={16} />
                <span className="hidden sm:inline">Search</span>
              </>
            )}
          </button>
        </div>

        {/* Media type filter toggle */}
        <div className="flex mt-3 gap-2">
          {[
            { value: 'all', label: 'All' },
            { value: 'movie', label: 'Movies' },
            { value: 'tv', label: 'TV Shows' },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setMediaTypeFilter(opt.value)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                mediaTypeFilter === opt.value
                  ? 'bg-red-600 border-red-600 text-white'
                  : 'border-gray-600 text-gray-400 hover:border-red-500 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-3 p-3 bg-red-900 border border-red-600 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Search Results */}
      <div
        className="max-h-96 overflow-y-auto dark-scrollbar"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#4B5563 #1F2937',
        }}
      >
        {searchResults.length > 0 ? (
          <div className="p-4">
            <p className="text-sm text-gray-400 mb-4">
              Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
            </p>
            <div className="space-y-3">
              {searchResults.map((movie, index) => (
                <button
                  key={index}
                  onClick={() => selectMovie(movie)}
                  className="w-full p-4 border border-gray-600 bg-black rounded-lg hover:border-red-500 transition-colors text-left"
                >
                  <div className="flex space-x-4">
                    {/* Movie Poster */}
                    <div className="w-16 h-24 bg-gray-800 rounded flex-shrink-0 flex items-center justify-center">
                      {movie.Poster && movie.Poster !== 'N/A' ? (
                        <img
                          src={movie.Poster}
                          alt={`${movie.Title} poster`}
                          className="w-full h-full object-cover rounded"
                          onError={e => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="text-2xl">🎬</div>
                      )}
                    </div>

                    {/* Movie Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-white">
                          {movie.Title} ({movie.Year})
                        </h3>
                        {movie.Type === 'tv_show' ? (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-blue-900 text-blue-300 flex-shrink-0">
                            TV
                          </span>
                        ) : (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 flex-shrink-0">
                            Movie
                          </span>
                        )}
                      </div>

                      <div className="space-y-1 text-sm text-gray-300">
                        {movie.Director && movie.Director !== 'N/A' && (
                          <div className="flex items-center space-x-1">
                            <User size={14} />
                            <span>
                              {movie.Type === 'tv_show' ? 'Created by' : 'Directed by'}{' '}
                              {movie.Director}
                            </span>
                          </div>
                        )}

                        {movie.Actors && movie.Actors !== 'N/A' && (
                          <div className="flex items-center space-x-1">
                            <span>🎭</span>
                            <span className="truncate">Starring: {movie.Actors}</span>
                          </div>
                        )}

                        {movie.Genre && movie.Genre !== 'N/A' && (
                          <div className="flex items-center space-x-1">
                            <span>🎭</span>
                            <span>{movie.Genre}</span>
                          </div>
                        )}

                        {movie.Runtime && movie.Runtime !== 'N/A' && (
                          <div className="flex items-center space-x-1">
                            <Clock size={14} />
                            <span>{movie.Runtime}</span>
                          </div>
                        )}

                        {movie.imdbRating && movie.imdbRating !== 'N/A' && (
                          <div className="flex items-center space-x-1">
                            <Star size={14} />
                            <span>IMDb: {movie.imdbRating}/10</span>
                          </div>
                        )}
                      </div>

                      {movie.Plot && movie.Plot !== 'N/A' && (
                        <p className="text-xs text-gray-400 mt-2 line-clamp-2">{movie.Plot}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : hasSearched && !isSearching ? (
          <div className="p-8 text-center text-gray-400">
            <Search className="mx-auto h-12 w-12 text-gray-500 mb-4" />
            <p>No results found for "{searchQuery}"</p>
            <p className="text-sm mt-1">Try a different search term</p>
          </div>
        ) : !hasSearched && !isSearching ? (
          <div className="p-8 text-center text-gray-400">
            <Search className="mx-auto h-12 w-12 text-gray-500 mb-4" />
            <p>Enter a title to search to add to catalog</p>
          </div>
        ) : null}
      </div>
    </BaseModal>
  );
};

export default MovieSearchModal;
